import { useMemo, useRef, useState } from "react";

import { SuccessToast, useSuccessFeedback } from "./useSuccessFeedback";
import CameraIcon from "./common/CameraIcon";
import EditIcon from "./common/EditIcon";
import ListTextPreview from "./common/ListTextPreview";
import TrashIcon from "./common/TrashIcon";
import { createAttachmentName, getRecordArea } from "../utils/attachmentNaming";
import { processImageFile } from "../utils/imageProcessing";
import { normalizeDamageCounts } from "../utils/damageRecording";

const conditions = ["Gut", "Leicht beschädigt", "Beschädigt", "Gesperrt"];
const palletTypes = [
  "Euro Palette",
  "Einweg Palette",
  "Gitterbox",
  "Kundenpalette",
  "Sonstige",
];

const initialForm = {
  type: "",
  selectedSupplier: "",
  packageCount: "",
  packageCondition: "Gut",
  palletCount: "",
  palletType: "",
  palletCondition: "Gut",
  palletExchanged: "",
  damageReport: "Nein",
  damagedPackages: 0,
  damagedPallets: 0,
  comment: "",
  photoData: "",
  photoDataList: [],
  deliveryNoteData: "",
  deliveryNoteDataList: [],
};

function getInitialForm(type = "") {
  return { ...initialForm, type };
}

function getStoredImages(record, listFieldName, legacyFieldName) {
  if (Array.isArray(record?.[listFieldName]) && record[listFieldName].length > 0) {
    return record[listFieldName];
  }

  return record?.[legacyFieldName] ? [record[legacyFieldName]] : [];
}

function getImageDownloadName(prefix, record, dataUrl, index) {
  const attachmentTypes = {
    foto: record.damageReport === "Ja" ? "SCHADEN" : "BILD",
    lieferschein: "LS",
    abd: "ABD",
  };
  return createAttachmentName({
    area: getRecordArea(record),
    processNumber: record.processNumber,
    attachmentType: attachmentTypes[prefix] || prefix,
    sequence: index + 1,
    dataUrl,
  });
}

function clampNumber(value) {
  return Math.max(0, Number(value) || 0);
}

function formatDuration(totalSeconds) {
  const seconds = Math.max(0, Number(totalSeconds) || 0);
  const minutes = Math.floor(seconds / 60);
  const restSeconds = seconds % 60;

  return `${minutes}m ${String(restSeconds).padStart(2, "0")}s`;
}

function isCertificateValid(supplier) {
  if (!supplier?.certificateData || !supplier?.certificateValidUntil) {
    return false;
  }

  const [year, month] = String(supplier.certificateValidUntil).split("-");
  const validUntil = new Date(Number(year), Number(month), 0, 23, 59, 59);

  if (Number.isNaN(validUntil.getTime())) {
    return false;
  }

  return validUntil >= new Date();
}

function isBlockedBioSupplier(supplier) {
  const bioMode = supplier?.bioMode || (supplier?.bio ? "bio" : "nein");

  return bioMode === "bio" && !isCertificateValid(supplier);
}

export default function WareneingangRetoure({
  pageTitle = "Wareneingang, Retoure und Postrückläufer",
  pageDescription = "Die Schnell-Erfassung ist fuer PC, Zebra und Handy auf moeglichst wenig Tippen ausgelegt.",
  recordTypeOptions = ["Wareneingang", "Retoure", "Postrückläufer"],
  defaultType = "",
  listTitle = "Gespeicherte Vorgaenge",
  records,
  suppliers,
  user,
  onSaveRecord,
  onDeleteRecord,
  onToggleProcessed,
  onAddAbdToRecord,
  showAbdColumn = false,
}) {
  const [form, setForm] = useState(() => getInitialForm(defaultType));
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterSupplier, setFilterSupplier] = useState("all");
  const [recordsVisible, setRecordsVisible] = useState(false);
  const [processStartedAt, setProcessStartedAt] = useState(null);
  const photoInputRef = useRef(null);
  const deliveryNoteInputRef = useRef(null);
  const { successVisible, triggerSuccessFeedback } = useSuccessFeedback();
  const initialFormForPage = useMemo(
    () => getInitialForm(defaultType),
    [defaultType]
  );

  const hasPallets = Number(form.palletCount) > 0;
  const selectedSupplierData = useMemo(() => {
    return suppliers.find((supplier) => supplier.name === form.selectedSupplier);
  }, [form.selectedSupplier, suppliers]);
  const suppliersByName = useMemo(() => {
    return new Map(suppliers.map((supplier) => [supplier.name, supplier]));
  }, [suppliers]);

  const filteredRecords = useMemo(() => {
    return [...records].reverse().filter((record) => {
      const matchesType = filterType === "all" || record.type === filterType;
      const matchesSupplier =
        filterSupplier === "all" || record.selectedSupplier === filterSupplier;
      const query = searchTerm.trim().toLowerCase();
      const matchesSearch =
        query.length === 0 ||
        record.type.toLowerCase().includes(query) ||
        record.selectedSupplier.toLowerCase().includes(query) ||
        String(suppliersByName.get(record.selectedSupplier)?.customerNumber || "")
          .toLowerCase()
          .includes(query);

      return matchesType && matchesSupplier && matchesSearch;
    });
  }, [filterSupplier, filterType, records, searchTerm, suppliersByName]);

  const setCountValue = (fieldName, nextValue) => {
    setForm((currentForm) => ({
      ...currentForm,
      [fieldName]: nextValue === "" ? "" : clampNumber(nextValue),
    }));
  };

  const handleSave = async () => {
    if (!form.type) {
      window.alert("Bitte waehle zuerst Wareneingang, Retoure oder Postruecklaeufer aus.");
      return;
    }

    if (!form.selectedSupplier) {
      window.alert("Bitte wähle zuerst einen Kunden aus.");
      return;
    }

    const packageCount = clampNumber(form.packageCount);
    const palletCount = clampNumber(form.palletCount);
    const damageCounts = normalizeDamageCounts(
      form.damageReport,
      form.damagedPackages,
      form.damagedPallets,
      packageCount,
      palletCount
    );

    if (!showAbdColumn && packageCount === 0 && palletCount === 0) {
      window.alert("Bitte trage mindestens 1 Paket oder mindestens 1 Palette ein.");
      return;
    }

    if (hasPallets && !form.palletType) {
      window.alert("Bitte wähle den Palettentyp aus.");
      return;
    }

    if (form.type === "Wareneingang" && isBlockedBioSupplier(selectedSupplierData)) {
      window.alert(
        "Dieser Kunde darf im BIO Wareneingang nicht bearbeitet werden, weil kein gueltiges BIO Zertifikat vorhanden ist."
      );
      return;
    }

    if (!damageCounts.valid) {
      window.alert(damageCounts.message);
      return;
    }

    try {
      await onSaveRecord({
        timestamp: new Date().toLocaleString("de-DE"),
        type: form.type,
        selectedSupplier: form.selectedSupplier,
        packageCount,
        packageCondition: packageCount > 0 ? form.packageCondition : "-",
        palletCount,
        palletType: hasPallets ? form.palletType : "-",
        palletCondition: hasPallets ? form.palletCondition : "-",
        palletExchanged: form.palletExchanged,
        damageReport: form.damageReport,
        damagedPackages: damageCounts.damagedPackages,
        damagedPallets: damageCounts.damagedPallets,
        comment: form.comment?.trim() || "",
        photoData: form.photoData,
        photoDataList: form.photoDataList,
        deliveryNoteData: form.deliveryNoteData,
        deliveryNoteDataList: form.deliveryNoteDataList,
        processDurationSeconds: processStartedAt
          ? Math.max(0, Math.round((Date.now() - processStartedAt) / 1000))
          : 0,
      });

      setForm(initialFormForPage);
      setProcessStartedAt(null);
      triggerSuccessFeedback();
    } catch (error) {
      window.alert(error.message);
    }
  };

  const handleDelete = async (recordId) => {
    try {
      await onDeleteRecord(recordId);
    } catch (error) {
      window.alert(error.message);
    }
  };

  const handleToggleProcessed = async (record) => {
    try {
      await onToggleProcessed(record.id, !record.processed);
    } catch (error) {
      window.alert(error.message);
    }
  };

  const handleAbdCapture = async (record, event) => {
    const files = Array.from(event.target.files || []);

    if (files.length === 0) {
      return;
    }

    try {
      const dataUrls = await Promise.all(files.map(processImageFile));
      await onAddAbdToRecord?.(record.id, dataUrls);
    } catch (error) {
      window.alert(error.message);
    } finally {
      event.target.value = "";
    }
  };

  const resetFilters = () => {
    setSearchTerm("");
    setFilterType("all");
    setFilterSupplier("all");
  };

  const handleSupplierChange = (supplierName) => {
    const supplier = suppliers.find((item) => item.name === supplierName);

    if (form.type === "Wareneingang" && isBlockedBioSupplier(supplier)) {
      window.alert(
        "Dieser Kunde darf im BIO Wareneingang nicht bearbeitet werden, weil kein gueltiges BIO Zertifikat vorhanden ist."
      );
      return;
    }

    setForm({ ...form, selectedSupplier: supplierName });
    setProcessStartedAt(supplierName ? Date.now() : null);
  };

  const handleImageCapture = async (event, fieldName) => {
    const files = Array.from(event.target.files || []);

    if (files.length === 0) {
      return;
    }

    try {
      const dataUrls = await Promise.all(files.map(processImageFile));
      const listFieldName = `${fieldName}List`;
      setForm((currentForm) => {
        const nextList = [
          ...(Array.isArray(currentForm[listFieldName])
            ? currentForm[listFieldName]
            : []),
          ...dataUrls,
        ];

        return {
          ...currentForm,
          [listFieldName]: nextList,
          [fieldName]: nextList[0] || "",
        };
      });
    } catch (error) {
      window.alert(error.message);
    } finally {
      event.target.value = "";
    }
  };

  const openStoredImage = (dataUrl) => {
    const imageWindow = window.open();

    if (!imageWindow) {
      window.alert("Bitte erlaube Pop-ups, damit das Bild geöffnet werden kann.");
      return;
    }

    imageWindow.document.write(
      `<img src="${dataUrl}" alt="Gespeichertes Bild" style="max-width:100%;height:auto;display:block;margin:0 auto;" />`
    );
    imageWindow.document.close();
  };

  const downloadStoredImage = (dataUrl, fileName) => {
    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="page-stack">
      <SuccessToast visible={successVisible} />
      <div>
        <p className="eyebrow">Erfassung</p>
        <h1 className="page-title">{pageTitle}</h1>
        <p className="section-text">{pageDescription}</p>
      </div>

      <section className="card">
        <div className="section-head">
          <div>
            <h2>Neuen Vorgang anlegen</h2>
            <p className="section-text">
              Wähle zuerst Typ und Kunde, danach Mengen und optional Fotos.
            </p>
          </div>
          <div className="entry-summary">
            <span>{form.type || "Typ waehlen"}</span>
            <span>{form.selectedSupplier || "Kein Kunde"}</span>
          </div>
        </div>

        <div className="quick-toggle-row">
          {recordTypeOptions.map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => setForm({ ...form, type })}
              className={`button ${
                form.type === type ? "button-primary" : "button-secondary"
              }`}
            >
              {type}
            </button>
          ))}
        </div>

        {form.type ? (
          <>
        <div className="form-grid columns-2 entry-form-grid">
          <label className="field field-wide">
            <span>Kunde</span>
            <select
              value={form.selectedSupplier}
              onChange={(event) => handleSupplierChange(event.target.value)}
            >
              <option value="">Bitte auswählen</option>
              {suppliers.map((supplier) => (
                <option key={supplier.id} value={supplier.name}>
                  {supplier.customerNumber
                    ? `${supplier.short} - ${supplier.customerNumber}`
                    : supplier.short}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span>Anzahl Pakete</span>
            <input
              type="number"
              min="0"
              value={form.packageCount}
              onChange={(event) =>
                setCountValue("packageCount", event.target.value)
              }
            />
          </label>

          <label className="field">
            <span>Zustand Pakete</span>
            <select
              value={form.packageCondition}
              onChange={(event) =>
                setForm({ ...form, packageCondition: event.target.value })
              }
            >
              {conditions.map((condition) => (
                <option key={condition} value={condition}>
                  {condition}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span>Anzahl Paletten</span>
            <input
              type="number"
              min="0"
              value={form.palletCount}
              onChange={(event) =>
                setCountValue("palletCount", event.target.value)
              }
            />
          </label>

          {hasPallets && (
            <>
              <label className="field">
                <span>Palettentyp</span>
                <select
                  value={form.palletType}
                  onChange={(event) =>
                    setForm({ ...form, palletType: event.target.value })
                  }
                >
                  <option value="">Bitte auswählen</option>
                  {palletTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </label>

              <label className="field">
                <span>Zustand Paletten</span>
                <select
                  value={form.palletCondition}
                  onChange={(event) =>
                    setForm({ ...form, palletCondition: event.target.value })
                  }
                >
                  {conditions.map((condition) => (
                    <option key={condition} value={condition}>
                      {condition}
                    </option>
                  ))}
                </select>
              </label>

              {showAbdColumn && (
                <div className="field">
                  <span>Palette wurde getauscht</span>
                  <div className="quick-toggle-row">
                    {["Ja", "Nein"].map((value) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() =>
                          setForm({ ...form, palletExchanged: value })
                        }
                        className={`button ${
                          form.palletExchanged === value
                            ? "button-primary"
                            : "button-secondary"
                        }`}
                      >
                        {value}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {!showAbdColumn && (
            <div className="field">
              <span>Schaden</span>
              <div className="quick-toggle-row">
                {["Ja", "Nein"].map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() =>
                      setForm({
                        ...form,
                        damageReport: value,
                        ...(value === "Nein"
                          ? { damagedPackages: 0, damagedPallets: 0 }
                          : {}),
                      })
                    }
                    className={`button ${
                      form.damageReport === value
                        ? "button-primary"
                        : "button-secondary"
                    }`}
                  >
                    {value}
                  </button>
                ))}
              </div>
            </div>
          )}

          {!showAbdColumn && form.damageReport === "Ja" && (
            <div className="field field-wide damage-scope">
              <span>Schadensumfang</span>
              <div className="form-grid columns-2">
                <label className="field">
                  <span>Beschädigte Pakete</span>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={form.damagedPackages}
                    onChange={(event) =>
                      setForm({ ...form, damagedPackages: event.target.value })
                    }
                  />
                </label>
                <label className="field">
                  <span>Beschädigte Paletten</span>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={form.damagedPallets}
                    onChange={(event) =>
                      setForm({ ...form, damagedPallets: event.target.value })
                    }
                  />
                </label>
              </div>
            </div>
          )}

          <label className="field field-wide">
            <span>Kommentar</span>
            <textarea
              rows="3"
              placeholder="Optionalen Kommentar eingeben"
              value={form.comment}
              onChange={(event) =>
                setForm({ ...form, comment: event.target.value })
              }
            />
          </label>

          <div className="field field-wide">
            <span>Foto zum Vorgang</span>
            <div className="capture-card">
              <div>
                <strong>
                  {form.photoDataList.length > 0
                    ? `${form.photoDataList.length} Foto(s) vorhanden`
                    : "Kein Foto aufgenommen"}
                </strong>
                <p className="section-text">
                  Optionales Foto für den erfassten Vorgang aufnehmen.
                </p>
              </div>
              <div className="inline-actions">
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="visually-hidden"
                  onChange={(event) => handleImageCapture(event, "photoData")}
                />
                <button
                  type="button"
                  onClick={() => photoInputRef.current?.click()}
                  className="button button-secondary"
                >
                  Foto aufnehmen
                </button>
                {form.photoData && (
                  <button
                    type="button"
                    onClick={() =>
                      setForm((currentForm) => {
                        const photoDataList = currentForm.photoDataList.slice(0, -1);

                        return {
                          ...currentForm,
                          photoDataList,
                          photoData: photoDataList[0] || "",
                        };
                      })
                    }
                    className="button button-inline-danger"
                  >
                    Letztes Foto entfernen
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="field field-wide">
            <span>Lieferschein</span>
            <div className="capture-card">
              <div>
                <strong>
                  {form.deliveryNoteData
                    ? `${form.deliveryNoteDataList.length} Lieferschein-Foto(s) vorhanden`
                    : "Kein Lieferschein fotografiert"}
                </strong>
                <p className="section-text">
                  Optional den Lieferschein direkt mit der Kamera fotografieren.
                </p>
              </div>
              <div className="inline-actions">
                <input
                  ref={deliveryNoteInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="visually-hidden"
                  onChange={(event) =>
                    handleImageCapture(event, "deliveryNoteData")
                  }
                />
                <button
                  type="button"
                  onClick={() => deliveryNoteInputRef.current?.click()}
                  className="button button-secondary"
                >
                  LS fotografieren
                </button>
                {form.deliveryNoteData && (
                  <button
                    type="button"
                    onClick={() =>
                      setForm((currentForm) => {
                        const deliveryNoteDataList =
                          currentForm.deliveryNoteDataList.slice(0, -1);

                        return {
                          ...currentForm,
                          deliveryNoteDataList,
                          deliveryNoteData: deliveryNoteDataList[0] || "",
                        };
                      })
                    }
                    className="button button-inline-danger"
                  >
                    Letzten LS entfernen
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="button-row action-bar-sticky">
          <button onClick={handleSave} className="button button-primary">
            Vorgang speichern
          </button>

          <button
            onClick={() => setForm(initialFormForPage)}
            className="button button-secondary"
          >
            Formular zurücksetzen
          </button>
        </div>
          </>
        ) : (
          <div className="empty-state">
            Bitte zuerst Wareneingang, Retoure oder Postruecklaeufer auswaehlen.
          </div>
        )}
      </section>

      <section className="card">
        <div className="section-head">
          <div>
            <h2>{listTitle}</h2>
            <p className="section-text">
              Hier kannst du nach Typ und Kunde filtern oder Einträge
              löschen.
            </p>
          </div>
          <div className="inline-actions">
            <button
              type="button"
              onClick={() => setRecordsVisible((current) => !current)}
              className="button button-dark mde-only-button"
            >
              {recordsVisible ? "Liste ausblenden" : "Liste anzeigen"}
            </button>
            <button onClick={resetFilters} className="button button-secondary">
              Filter zurücksetzen
            </button>
          </div>
        </div>

        <div
          className={`mde-records-body ${
            recordsVisible ? "mde-records-expanded" : "mde-records-collapsed"
          }`}
        >
          <div className="form-grid columns-3">
            <label className="field">
              <span>Suche</span>
              <input
                placeholder="Nach Typ oder Kunde suchen"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </label>

            <label className="field">
              <span>Typ filtern</span>
              <select
                value={filterType}
                onChange={(event) => setFilterType(event.target.value)}
              >
                <option value="all">Alle Typen</option>
                {recordTypeOptions.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </label>

            <label className="field">
              <span>Kunde filtern</span>
              <select
                value={filterSupplier}
                onChange={(event) => setFilterSupplier(event.target.value)}
              >
                <option value="all">Alle Kunden</option>
                {suppliers.map((supplier) => (
                  <option key={supplier.id} value={supplier.name}>
                    {supplier.customerNumber
                      ? `${supplier.short} - ${supplier.customerNumber}`
                      : supplier.short}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {filteredRecords.length === 0 ? (
            <div className="empty-state">Keine passenden Einträge gefunden.</div>
          ) : (
            <div className="table-wrap">
              <table className="data-table wareneingang-table">
                <thead>
                  <tr>
                    <th>Zeit</th>
                    <th>Typ</th>
                    <th>Kunde</th>
                    <th>Kundennummer</th>
                    <th>Pakete</th>
                    <th>Zustand Pakete</th>
                    <th>Paletten</th>
                    <th>Palettentyp</th>
                    <th>Zustand Paletten</th>
                    {!showAbdColumn && <th>Beschädigte Pakete</th>}
                    {!showAbdColumn && <th>Beschädigte Paletten</th>}
                    <th>Kommentar</th>
                    <th>Bearbeitungszeit</th>
                    <th>Foto</th>
                    <th>Lieferschein</th>
                    {showAbdColumn && <th>ABD</th>}
                    <th>Bearbeitet</th>
                    <th>Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRecords.map((record) => (
                    <tr
                      key={record.id}
                      className={record.processed ? "row-processed" : ""}
                    >
                      <td>{record.timestamp}</td>
                      <td>{record.type}</td>
                      <td>{record.selectedSupplier}</td>
                      <td>
                        {suppliersByName.get(record.selectedSupplier)?.customerNumber ||
                          "-"}
                      </td>
                      <td>{record.packageCount}</td>
                      <td>{record.packageCondition}</td>
                      <td>{record.palletCount}</td>
                      <td>{record.palletType}</td>
                      <td>{record.palletCondition}</td>
                      {!showAbdColumn && (
                        <td>{Number(record.damagedPackages) || 0}</td>
                      )}
                      {!showAbdColumn && (
                        <td>{Number(record.damagedPallets) || 0}</td>
                      )}
                      <td><ListTextPreview value={record.comment} /></td>
                      <td>{formatDuration(record.processDurationSeconds)}</td>
                      <td>
                        {getStoredImages(record, "photoDataList", "photoData").length > 0 ? (
                          <div className="media-actions">
                            {getStoredImages(record, "photoDataList", "photoData").map(
                              (imageData, photoIndex) => (
                                <div
                                  key={`${record.id}-photo-${photoIndex}`}
                                  className="media-actions"
                                >
                                  <button
                                    type="button"
                                    onClick={() => openStoredImage(imageData)}
                                    className="button button-inline-edit button-image-open"
                                    aria-label={`Bild ${photoIndex + 1} ansehen`}
                                    title={`Bild ${photoIndex + 1}`}
                                  >
                                    <CameraIcon />
                                    <span>{photoIndex + 1}</span>
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      downloadStoredImage(
                                        imageData,
                                        getImageDownloadName(
                                          "foto",
                                          record,
                                          imageData,
                                          photoIndex
                                        )
                                      )
                                    }
                                    className="button button-icon-download"
                                    aria-label={`Foto ${photoIndex + 1} herunterladen`}
                                  >
                                    ↓
                                  </button>
                                </div>
                              )
                            )}
                          </div>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>
                        {getStoredImages(record, "deliveryNoteDataList", "deliveryNoteData").length > 0 ? (
                          <div className="media-actions">
                            {getStoredImages(
                              record,
                              "deliveryNoteDataList",
                              "deliveryNoteData"
                            ).map((imageData, photoIndex) => (
                              <div
                                key={`${record.id}-delivery-${photoIndex}`}
                                className="media-actions"
                              >
                                <button
                                  type="button"
                                  onClick={() => openStoredImage(imageData)}
                                  className="button button-inline-edit button-image-open"
                                  aria-label={`Bild ${photoIndex + 1} ansehen`}
                                  title={`Bild ${photoIndex + 1}`}
                                >
                                  <CameraIcon />
                                  <span>{photoIndex + 1}</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() =>
                                    downloadStoredImage(
                                      imageData,
                                      getImageDownloadName(
                                        "lieferschein",
                                        record,
                                        imageData,
                                        photoIndex
                                      )
                                    )
                                  }
                                  className="button button-icon-download"
                                  aria-label={`Lieferschein ${photoIndex + 1} herunterladen`}
                                >
                                  ↓
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          "-"
                        )}
                      </td>
                      {showAbdColumn && (
                        <td>
                          <div className="media-actions">
                            <input
                              id={`abd-input-${record.id}`}
                              type="file"
                              accept="image/*"
                              capture="environment"
                              multiple
                              className="visually-hidden"
                              onChange={(event) => handleAbdCapture(record, event)}
                            />
                            <button
                              type="button"
                              onClick={() =>
                                document.getElementById(`abd-input-${record.id}`)?.click()
                              }
                              className="button button-secondary"
                              aria-label="ABD hinzufuegen"
                              title="ABD hinzufuegen"
                            >
                              ABD
                            </button>
                            {getStoredImages(record, "abdDataList", "abdData").map(
                              (imageData, abdIndex) => (
                                <div
                                  key={`${record.id}-abd-${abdIndex}`}
                                  className="media-actions"
                                >
                                  <button
                                    type="button"
                                    onClick={() => openStoredImage(imageData)}
                                    className="button button-inline-edit button-image-open"
                                    aria-label={`ABD ${abdIndex + 1} ansehen`}
                                    title={`ABD ${abdIndex + 1}`}
                                  >
                                    <CameraIcon />
                                    <span>{abdIndex + 1}</span>
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      downloadStoredImage(
                                        imageData,
                                        getImageDownloadName(
                                          "abd",
                                          record,
                                          imageData,
                                          abdIndex
                                        )
                                      )
                                    }
                                    className="button button-icon-download"
                                    aria-label={`ABD ${abdIndex + 1} herunterladen`}
                                  >
                                    â†“
                                  </button>
                                </div>
                              )
                            )}
                          </div>
                        </td>
                      )}
                      <td>
                        <button
                          type="button"
                          onClick={() => handleToggleProcessed(record)}
                          aria-label="Bearbeitet umschalten"
                          title="Bearbeitet"
                          className={`button button-inline-processed ${
                            record.processed ? "button-inline-processed-active" : ""
                          }`}
                        >
                          <EditIcon />
                        </button>
                      </td>
                      <td>
                        {user?.isAdmin ? (
                          <button
                            onClick={() => handleDelete(record.id)}
                            className="button button-inline-danger button-icon-danger"
                            aria-label="Loeschen"
                            title="Loeschen"
                          >
                            <TrashIcon />
                          </button>
                        ) : (
                          <span className="section-text">Keine Berechtigung</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
